import { API_BASE_URL } from "./config";
import { clearCompanionSession } from "../auth/session";
import {
  getDashboardLoginPath,
  getDashboardOverviewPath,
  isDashboardLoginPath,
  normalizePathname,
} from "../utils/dashboardPaths";

export class ApiError extends Error {
  public readonly status: number;

  public readonly bodyText: string;

  constructor(message: string, status: number, bodyText: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.bodyText = bodyText;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  const base = API_BASE_URL.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}${p}`);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, value);
      }
    });
  }
  return url.toString();
}

function handleUnauthorized401(): void {
  clearCompanionSession();
  // Avoid an infinite redirect loop if we're already on the login route.
  if (!isDashboardLoginPath(window.location.pathname)) {
    window.location.assign(getDashboardLoginPath());
  }
}

/** JSON envelope `code: 403` ("Wait list not confirmed") or HTTP 403: show invite flow on overview. */
function handleWaitlist403(): void {
  let waitlistWasAlreadySet = false;
  try {
    waitlistWasAlreadySet = localStorage.getItem("waitlist") === "true";
    localStorage.setItem("waitlist", "true");
  } catch {
    /* ignore quota / private mode */
  }
  const dest = getDashboardOverviewPath();
  const here = normalizePathname(window.location.pathname);
  const there = normalizePathname(dest);
  if (here === there) {
    // First 403 on overview: reload so Dashboard picks up `waitlist` from storage.
    // Later 403s (e.g. create_once / user/details while still waitlisted): do not reload or we loop forever.
    if (!waitlistWasAlreadySet) {
      window.location.reload();
    }
    return;
  }
  window.location.assign(dest);
}

export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", headers = {}, body, query, signal } = options;
  const url = path.startsWith("http") ? path : buildUrl(path, query);

  const init: RequestInit = {
    method,
    headers: {
      Accept: "application/json",
      ...headers,
    },
    signal,
  };

  const isBodyJson = body !== undefined && method !== "GET";
  if (isBodyJson) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      if (response.status === 403) {
        handleWaitlist403();
      }
      throw new ApiError("Invalid JSON response", response.status, text);
    }
  }

  // Backend sends `{ code: number }` in the response envelope for all endpoints.
  // When the code indicates waitlist (no invite), send the user to the dashboard overview.
  // When the code indicates auth failure, we must clear the session and force re-login.
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    if (code === 403 || code === "403") {
      handleWaitlist403();
    }
    if (code === 401 || code === "401") {
      handleUnauthorized401();
    }
  }

  if (!response.ok) {
    if (response.status === 403) {
      handleWaitlist403();
    }
    throw new ApiError(`HTTP ${response.status}`, response.status, text);
  }

  return data as T;
}
